import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Calendar, Clock, ChevronLeft, ChevronRight, Video, Check, User, MapPin, Loader2 } from 'lucide-react';
import VerifiedBadge from '@/components/VerifiedBadge';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TourBookingPage() {
  const { propertyId, agentId } = useParams();
  const { token } = useAuth();
  const { language } = useLanguage();
  const [property, setProperty] = useState(null);
  const [agent, setAgent] = useState(null);
  const [slotsData, setSlotsData] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;
  const dayNames = language === 'es'
    ? ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom']
    : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getWeekStart = (offset) => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1 + offset * 7);
    return d.toISOString().split('T')[0];
  };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/properties/${propertyId}`),
      axios.get(`${API}/verification/public/${agentId}`).catch(() => ({ data: {} })),
    ]).then(([propRes, agentRes]) => {
      setProperty(propRes.data);
      setAgent(agentRes.data);
    }).catch(() => toast.error(tt('Failed to load', 'Error al cargar')))
    .finally(() => setLoading(false));
  }, [propertyId, agentId]);

  useEffect(() => {
    const ws = getWeekStart(weekOffset);
    axios.get(`${API}/tours/slots/${agentId}?week_start=${ws}`)
      .then(r => setSlotsData(r.data))
      .catch(() => {});
  }, [agentId, weekOffset]);

  const confirmBooking = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    try {
      await axios.post(`${API}/tours/book`, {
        property_id: propertyId,
        agent_id: agentId,
        date: selectedSlot.date,
        time: selectedSlot.time,
        notes,
      }, { headers });
      toast.success(tt('Tour requested! The agent will confirm shortly.', 'Tour solicitado! El agente confirmara pronto.'));
      setShowConfirm(false);
      setSelectedSlot(null);
      // Refresh slots
      const ws = getWeekStart(weekOffset);
      const r = await axios.get(`${API}/tours/slots/${agentId}?week_start=${ws}`);
      setSlotsData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Booking failed', 'Error al reservar'));
    }
    setBooking(false);
  };

  if (loading) return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-8 w-48" /><Skeleton className="h-64 rounded-xl" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tour-booking-page">
      <Link to={`/property/${propertyId}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-4">
        <ArrowLeft className="w-4 h-4" />{tt('Back to Property', 'Volver a Propiedad')}
      </Link>

      {/* Property & Agent Header */}
      <div className="flex items-start gap-4 mb-6">
        <Video className="w-8 h-8 text-primary mt-1" />
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-semibold">{tt('Book a Virtual Tour', 'Reservar Tour Virtual')}</h1>
          {property && <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{property.title} - {property.city}</p>}
          {agent && (
            <div className="flex items-center gap-1.5 mt-1">
              <User className="w-3.5 h-3.5 text-slate-400" />
              <span className="text-sm text-slate-600">{agent.name}</span>
              {agent.verified && <VerifiedBadge role="agent" size="xs" />}
            </div>
          )}
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))} disabled={weekOffset === 0}
          className="gap-1" data-testid="prev-week">
          <ChevronLeft className="w-4 h-4" />{tt('Prev', 'Ant')}
        </Button>
        <p className="text-sm font-medium text-slate-600">
          {slotsData?.week_start ? new Date(slotsData.week_start + 'T00:00:00').toLocaleDateString(language === 'es' ? 'es-VE' : 'en-US', { month: 'long', day: 'numeric' }) : ''}
          {' - '}
          {slotsData?.days?.[6]?.date ? new Date(slotsData.days[6].date + 'T00:00:00').toLocaleDateString(language === 'es' ? 'es-VE' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
        </p>
        <Button variant="outline" size="sm" onClick={() => setWeekOffset(weekOffset + 1)} disabled={weekOffset >= 4}
          className="gap-1" data-testid="next-week">
          {tt('Next', 'Sig')}<ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2" data-testid="tour-calendar">
        {slotsData?.days?.map((day, idx) => {
          const dateObj = new Date(day.date + 'T00:00:00');
          const isToday = day.date === new Date().toISOString().split('T')[0];
          return (
            <div key={day.date} className={`rounded-xl border p-2 min-h-[200px] ${isToday ? 'border-primary bg-primary/5' : 'border-border'}`}>
              <div className="text-center mb-2">
                <p className={`text-[10px] font-semibold uppercase ${isToday ? 'text-primary' : 'text-slate-400'}`}>{dayNames[idx]}</p>
                <p className={`text-sm font-bold ${isToday ? 'text-primary' : 'text-slate-700'}`}>{dateObj.getDate()}</p>
              </div>
              <div className="space-y-1 max-h-[160px] overflow-y-auto">
                {day.slots.length > 0 ? day.slots.map(slot => (
                  <button key={slot.time} disabled={!slot.available}
                    onClick={() => { setSelectedSlot({ date: day.date, time: slot.time, dayName: dayNames[idx] }); setShowConfirm(true); }}
                    className={`w-full text-[10px] py-1 px-1 rounded transition-all ${
                      selectedSlot?.date === day.date && selectedSlot?.time === slot.time
                        ? 'bg-[#D4AF37] text-white'
                        : slot.available
                          ? 'bg-white border border-slate-200 hover:border-primary hover:bg-primary/5 text-slate-600 cursor-pointer'
                          : slot.booked
                            ? 'bg-red-50 text-red-300 border border-red-100 cursor-not-allowed'
                            : 'bg-slate-50 text-slate-300 cursor-not-allowed'
                    }`}
                    data-testid={`slot-${day.date}-${slot.time}`}>
                    {slot.time}
                  </button>
                )) : (
                  <p className="text-[9px] text-slate-300 text-center mt-4">{tt('No hours', 'Sin horas')}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 justify-center">
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><div className="w-3 h-3 rounded bg-white border border-slate-200" />{tt('Available', 'Disponible')}</div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><div className="w-3 h-3 rounded bg-red-50 border border-red-100" />{tt('Booked', 'Ocupado')}</div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500"><div className="w-3 h-3 rounded bg-slate-50" />{tt('Past', 'Pasado')}</div>
      </div>

      {/* Booking Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm" aria-describedby="book-confirm-desc">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" />{tt('Confirm Tour', 'Confirmar Tour')}</DialogTitle></DialogHeader>
          <p id="book-confirm-desc" className="text-sm text-slate-500">{tt('Book a 30-minute virtual tour with the agent.', 'Reserve un tour virtual de 30 minutos con el agente.')}</p>
          {selectedSlot && (
            <div className="space-y-3 mt-2">
              <div className="bg-primary/5 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">{selectedSlot.dayName}</p>
                <p className="font-heading font-bold text-lg text-slate-900">{new Date(selectedSlot.date + 'T00:00:00').toLocaleDateString(language === 'es' ? 'es-VE' : 'en-US', { month: 'long', day: 'numeric' })}</p>
                <p className="text-primary font-semibold">{selectedSlot.time} - {(() => {
                  const [h, m] = selectedSlot.time.split(':').map(Number);
                  const end = new Date(2026, 0, 1, h, m + 30);
                  return end.toTimeString().substring(0, 5);
                })()}</p>
                <div className="flex items-center justify-center gap-1 mt-1 text-xs text-slate-400"><Clock className="w-3 h-3" />30 min</div>
              </div>
              <div>
                <Label className="text-xs">{tt('Notes for the agent (optional)', 'Notas para el agente (opcional)')}</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="mt-1" placeholder={tt('Any preferences or questions...', 'Preferencias o preguntas...')} data-testid="tour-notes" />
              </div>
              <Button onClick={confirmBooking} disabled={booking}
                className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-2" data-testid="confirm-tour-btn">
                {booking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {booking ? tt('Booking...', 'Reservando...') : tt('Request Tour', 'Solicitar Tour')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
